/**
 * Auth endpoints. Deliberately does not go through `lib/api/client.ts`:
 * the client's refresh-on-401 path calls `refresh()`, and routing that back
 * through the client would recurse.
 */
import { platform } from '@kapture/platform';
import { ApiError, NetworkError, parseErrorBody } from '@kapture/api-client';

export interface AuthUser {
  id: string;
  org_id: string;
  email: string;
  name: string;
  role: string;
}

/**
 * Server response for both /auth/login and /auth/refresh. Note the nesting:
 * spec §4 describes a flat `{access_token, refresh_token, user}`, but the
 * implemented `LoggedInResponse` wraps the pair. The implementation is what
 * ships, so this follows it.
 */
interface LoggedInResponse {
  user: AuthUser;
  tokens: {
    access_token: string;
    refresh_token: string;
    token_type: string;
    access_expires_in: number;
  };
}

export interface AuthResult {
  user: AuthUser;
  accessToken: string;
  refreshToken: string;
  accessExpiresIn: number;
}

async function post(path: string, body: unknown): Promise<AuthResult> {
  let res: Response;
  try {
    res = await fetch(`${platform().apiBaseUrl}${path}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify(body),
    });
  } catch (err) {
    // No response at all — offline, DNS, TLS. Distinct from a rejection by
    // the server, because the caller reacts to them differently.
    throw new NetworkError(err);
  }

  if (!res.ok) {
    throw new ApiError(res.status, await parseErrorBody(res));
  }

  const data = (await res.json()) as LoggedInResponse;
  return {
    user: data.user,
    accessToken: data.tokens.access_token,
    refreshToken: data.tokens.refresh_token,
    accessExpiresIn: data.tokens.access_expires_in,
  };
}

export function login(email: string, password: string): Promise<AuthResult> {
  return post('/auth/login', { email, password });
}

export function refresh(refreshToken: string): Promise<AuthResult> {
  return post('/auth/refresh', { refresh_token: refreshToken });
}
